import { v4 as uuid } from "uuid";
import { z } from "zod";
import { Effects } from "../../lib/effects";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import {
  SerializationSchema
} from "../../serialization";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { ListControl } from "./list-control";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::list";
  static schema({ itemDef }) {
    const type = z.literal(Definition.type);
    const config = z.object({
      type: itemDef,
      label: z.string().optional(),
      getItemLabel: z.function().args(z.any()).returns(z.any()).optional()
    });
    const definition = z.object({
      type,
      config
    });
    return { type, config, definition };
  }
  static deserialize(data, deserializeCallback) {
    if (data.type !== Definition.type)
      throw new Error(`List: expected '${Definition.type}', got '${data.type}'`);
    const {
      config: { type, ...config }
    } = this.schema({
      itemDef: SerializationSchema.deserializedRecord
    }).definition.parse(data);
    const itemDef = deserializeCallback(type);
    return new ListDefinition({ ...config, type: itemDef });
  }
  get itemDef() {
    return this.config.type;
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    const item = this.itemDef.schema;
    const data = z.array(
      z.object({
        id: z.string(),
        type: item.type.optional(),
        value: item.data
      })
    );
    const value = z.array(item.value);
    const resolvedValue = z.array(item.resolvedValue);
    return {
      ...Definition.schema({ itemDef: item.definition }),
      data,
      value,
      resolvedValue
    };
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    return data?.map(({ value }) => this.itemDef.fromData(value));
  }
  toData(value) {
    return value.map((value2) => ({
      id: uuid(),
      type: this.itemDef.controlType,
      value: this.itemDef.toData(value2)
    }));
  }
  copyData(data, context) {
    return data?.map((item) => ({
      ...item,
      value: this.itemDef.copyData(item.value, context)
    }));
  }
  getTranslatableData(data) {
    if (data == null)
      return null;
    return Object.fromEntries(
      data.map((item) => [
        item.id,
        this.itemDef.getTranslatableData(item.value)
      ])
    );
  }
  mergeTranslatedData(data, translatedData, context) {
    if (translatedData == null)
      return data;
    return data.map((item) => {
      return {
        ...item,
        value: this.itemDef.mergeTranslatedData(
          item.value,
          translatedData[item.id],
          context
        )
      };
    });
  }
  resolveValue(data, resolver, stylesheet, control) {
    const effects = new Effects();
    const childControls = control?.childControls(data?.map(({ id }) => id));
    if (childControls) {
      effects.add(() => control?.setChildControls(childControls));
    }
    const itemValues = data?.map(
      ({ value, id }) => this.itemDef.resolveValue(
        value,
        resolver,
        stylesheet.child(id),
        childControls?.get(id)
      )
    );
    const stableValue = StableValue({
      name: Definition.type,
      read: () => itemValues?.map((v) => v.readStable()) ?? [],
      deps: itemValues
    });
    return {
      ...stableValue,
      triggerResolve: async (currentValue) => {
        await Promise.all([
          effects.run(),
          ...itemValues?.map((v, i) => v.triggerResolve(currentValue?.[i])) ?? []
        ]);
      }
    };
  }
  createInstance(sendMessage) {
    return new ListControl(this, sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
  introspect(data, target) {
    return data?.flatMap((item) => this.itemDef.introspect(item.value, target)) ?? [];
  }
}
class ListDefinition extends Definition {
}
function List(config) {
  return new ListDefinition(config);
}
export {
  List,
  ListDefinition
};
//# sourceMappingURL=list.js.map